/**
 * Height-feedback relayout — Step 7 of the IR build.
 *
 * See: docs/IR_RULE_MAPPING.md §3.5 (Rule S-G.a — leaf heights bucket to
 *      {100, 110, 120, 130, 140, 150}), §7 step 7.
 *
 * PROBLEM
 * ───────
 * The first `layoutIR()` pass has to size every Leaf before the DOM exists,
 * so it uses the default bucket (100). After the React tree renders, each
 * leaf's actual CSS-rendered height becomes available. Containers (Switch
 * cases, Fork branches, DoWhile body, top-level sequence) must grow to
 * accommodate the real heights — otherwise cards will visually overrun
 * their parent's bbox and the connection routing (step 6) will cross
 * through downstream nodes.
 *
 * SOLUTION
 * ────────
 * `relayout(laidWrapper, heightMap)`:
 *   1. Walks the previously-laid-out wrapper and produces a fresh IR tree
 *      with `measuredH` stamped on every Leaf whose id appears in
 *      `heightMap` (value is bucketed to the nearest {100..150} step).
 *   2. Re-runs `layoutIR()` on the stamped tree — the layout pass already
 *      honors `leaf.measuredH` in `layoutLeaf()` (step 4 handled this).
 *
 * The input wrapper is NOT mutated; a fresh `{start, sequence, end, bbox}`
 * tree is returned.
 *
 * IDEMPOTENCE
 * ───────────
 * `relayout(relayout(w, m), m)` must equal `relayout(w, m)` — calling
 * twice with the same measurement map produces identical geometry. The
 * bucketing step is the key: any height in `[91..100]` collapses to 100,
 * `[101..110]` to 110, and so on. So measurement noise within a bucket
 * does not thrash the layout across renders.
 *
 * Unknown leaf ids in the map are silently ignored (callers may hold
 * measurements for a previous workflow version without breaking).
 *
 * ABSENT LEAF MEASUREMENTS
 * ────────────────────────
 * If a leaf's id is NOT in `heightMap`, its existing `measuredH` is
 * preserved from the input tree. On a fresh tree (no prior measurement),
 * `layoutLeaf` falls back to LAYOUT_DIMS.LEAF_H_DEFAULT (100).
 */

import { IR_KIND } from './kinds';
import { layoutIR } from './layoutIR';

// ───────────────────────────────────────────────────────────────────
//  Public API
// ───────────────────────────────────────────────────────────────────

/**
 * Canonical leaf-height buckets (Rule S-G.a). The array is exported so
 * tests and the renderer can sanity-check that every leaf's final height
 * is one of these values.
 *
 * @type {ReadonlyArray<number>}
 */
export const LEAF_HEIGHT_BUCKETS = Object.freeze([100, 110, 120, 130, 140, 150]);

/**
 * Round a DOM-measured pixel height up to the nearest canonical bucket
 * value. Values ≤ 100 return 100; values ≥ 150 return 150. Non-finite or
 * non-positive inputs return 100 defensively.
 *
 * Rule S-G.a rationale: bucketing absorbs minor sub-pixel rendering
 * variance so two re-renders of the same leaf don't produce different
 * layouts. The bucket set covers every leaf variant Conductor OSS produces.
 *
 * @param {number} measured     Raw `getBoundingClientRect().height`
 * @returns {number}            One of {100, 110, 120, 130, 140, 150}
 */
export function bucketHeight(measured) {
  if (typeof measured !== 'number' || !Number.isFinite(measured) || measured <= 0) {
    return LEAF_HEIGHT_BUCKETS[0];
  }
  const rounded = Math.ceil(measured / 10) * 10;
  if (rounded <= LEAF_HEIGHT_BUCKETS[0]) return LEAF_HEIGHT_BUCKETS[0];
  const max = LEAF_HEIGHT_BUCKETS[LEAF_HEIGHT_BUCKETS.length - 1];
  if (rounded >= max) return max;
  return rounded;
}

/**
 * Produce a fresh laid-out wrapper with leaf heights refreshed from the
 * supplied `heightMap`.
 *
 * @param {{start:object, sequence:object[], end:object|null, bbox:object}} laid
 * @param {Object.<string, number>} [heightMap]  id → measured CSS px
 * @returns {{start:object, sequence:object[], end:object|null, bbox:object}}
 */
export function relayout(laid, heightMap = {}) {
  if (!laid || !laid.start || !Array.isArray(laid.sequence)) {
    throw new Error('relayout: expected a laid-out wrapper { start, sequence, end, bbox }');
  }
  if (heightMap == null || typeof heightMap !== 'object') {
    throw new Error('relayout: heightMap must be a plain object or undefined');
  }

  const stamped = {
    start: stampNode(laid.start, heightMap),
    sequence: laid.sequence.map(n => stampNode(n, heightMap)),
    end: laid.end ? stampNode(laid.end, heightMap) : null,
  };
  return layoutIR(stamped);
}

// ───────────────────────────────────────────────────────────────────
//  Tree-clone pass that stamps measuredH on Leaf nodes
// ───────────────────────────────────────────────────────────────────

/**
 * Recursively clone an IR (or laid-out IR) node, stamping `measuredH` on
 * every Leaf whose id has a fresh entry in `heightMap`.
 *
 * Layout fields (`bbox`, `headBBox`, `frameBBox`, `subtreeWidth`,
 * `localTrunkX`) are left on the clone — `layoutIR` overwrites them on
 * the return trip, so carrying them along is harmless and simplifies the
 * copy.
 *
 * @param {object} node
 * @param {Object.<string, number>} heightMap
 * @returns {object}
 */
function stampNode(node, heightMap) {
  switch (node.kind) {
    case IR_KIND.LEAF: {
      const cloned = { ...node };
      if (Object.prototype.hasOwnProperty.call(heightMap, node.id)) {
        cloned.measuredH = bucketHeight(heightMap[node.id]);
      }
      return cloned;
    }

    case IR_KIND.SWITCH: {
      return {
        ...node,
        cases: node.cases.map(c => cloneCase(c, heightMap)),
        defaultCase: node.defaultCase ? cloneCase(node.defaultCase, heightMap) : null,
      };
    }

    case IR_KIND.FORK: {
      return {
        ...node,
        branches: node.branches.map(b => cloneBranch(b, heightMap)),
      };
    }

    case IR_KIND.DYNAMIC_FORK: {
      return {
        ...node,
        resolvedBranches: node.resolvedBranches
          ? node.resolvedBranches.map(b => cloneBranch(b, heightMap))
          : null,
      };
    }

    case IR_KIND.DO_WHILE: {
      return {
        ...node,
        body: node.body.map(child => stampNode(child, heightMap)),
      };
    }

    case IR_KIND.SUB_WORKFLOW: {
      return {
        ...node,
        embedded: node.embedded ? stampNode(node.embedded, heightMap) : null,
      };
    }

    default:
      // TERMINATE, START, END — no children, nothing to stamp.
      return { ...node };
  }
}

function cloneCase(c, heightMap) {
  return {
    ...c,
    children: c.children.map(child => stampNode(child, heightMap)),
  };
}

function cloneBranch(b, heightMap) {
  return {
    ...b,
    children: b.children.map(child => stampNode(child, heightMap)),
  };
}

// ───────────────────────────────────────────────────────────────────
//  Helper: collect current measured heights from a laid-out wrapper
// ───────────────────────────────────────────────────────────────────

/**
 * Extract the current `measuredH` of every Leaf in a laid-out wrapper.
 * Useful for building an initial `heightMap` from a previous pass, for
 * idempotence checks, or for debugging.
 *
 * @param {{start:object, sequence:object[], end:object|null}} laid
 * @returns {Object.<string, number>}
 */
export function extractHeightMap(laid) {
  const out = {};
  const visit = (node) => {
    if (!node) return;
    if (node.kind === IR_KIND.LEAF && typeof node.measuredH === 'number') {
      out[node.id] = node.measuredH;
    }
    switch (node.kind) {
      case IR_KIND.SWITCH:
        node.cases.forEach(c => c.children.forEach(visit));
        if (node.defaultCase) node.defaultCase.children.forEach(visit);
        break;
      case IR_KIND.FORK:
        node.branches.forEach(b => b.children.forEach(visit));
        break;
      case IR_KIND.DYNAMIC_FORK:
        if (node.resolvedBranches) node.resolvedBranches.forEach(b => b.children.forEach(visit));
        break;
      case IR_KIND.DO_WHILE:
        node.body.forEach(visit);
        break;
      case IR_KIND.SUB_WORKFLOW:
        if (node.embedded) visit(node.embedded);
        break;
      default:
        break;
    }
  };
  visit(laid.start);
  laid.sequence.forEach(visit);
  if (laid.end) visit(laid.end);
  return out;
}
